import React, { useState, useCallback } from "react";
import { Image } from "react-bootstrap";
import { useAtom } from "jotai";

import "./GameDetailsDrawer.scss";
import BottomDrawer from "../../BottomDrawer/BottomDrawer";
import { GameMode } from "../../../utils/constants";
import { gameDetailsDrawerSlugAtom } from "../../../jotai/atoms/games";
import useAuth from "../../../hooks/useAuth";
import useSubscription from "../../../react-query/queries/use-subscription-status";
import useGameDetailsBySlugQuery from "../../../react-query/queries/use-game-details-by-slug";
import usePlaySoloGameMutation from "../../../react-query/mutations/use-play-solo-game";
import usePlatformDuelEntryMutation from "../../../react-query/mutations/use-platform-duel-entry";
import { StakeCurrency, Game, GameType } from "@nestcoinco/hyper-api-gateway-api-client";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";
import PlayIcon from "../../icons/PlayIcon";
import UploadIcon from "../../icons/UploadIcon";
import CheckmarkCircleIcon from "../../icons/CheckmarkCircleIcon";
import ImageFallbackThumbnail from "../../../images/fallback-thumbnail.png";
import FallbackAvatar from "../../../images/fallback-avatar.png";
import OneVsOneImage from "../../../images/1v1.svg";
import { formatPlayCount, capitalizeFirstLetter } from "../../../utils/helpers";
import { useShareGameLinkAction } from "../../../hooks/useGamesPage";
import { showErrorToast } from "../../../utils/show-toast";

function useGamePlay(
  game: Game | undefined,
  selectedMode: GameMode,
  onSuccess: () => void
) {
  const { mutation: soloMutation, isPending: isSoloPending } =
    usePlaySoloGameMutation({ gameId: game?.id || "" });

  const { mutation: duelMutation, playBattleGame } =
    usePlatformDuelEntryMutation();

  const handlePlay = useCallback(() => {
    if (selectedMode === GameMode.SOLO) {
      soloMutation.mutate(undefined, {
        onSuccess,
        onError: () => showErrorToast("Failed to start solo game"),
      });
      return;
    }

    if (!game?.id) {
      showErrorToast("Unable to start game. Please try again.");
      return;
    }

    playBattleGame(game.id, StakeCurrency.HPR, onSuccess);
  }, [game?.id, selectedMode, soloMutation, playBattleGame, onSuccess]);

  return {
    handlePlay,
    isPending: isSoloPending || duelMutation.isPending,
  };
}

function MediaHeader({ game }: { game: Game }) {
  return (
    <div className="drawer-header-media">
      <Image
        src={game.thumbnail || ImageFallbackThumbnail}
        alt=""
        role="presentation"
        className="media-bg"
        onError={(e) => {
          e.currentTarget.src = ImageFallbackThumbnail;
        }}
      />
      <div className="media-overlay" />
      <div className="game-icon-container">
        <Image
          src={game.thumbnail || ImageFallbackThumbnail}
          alt={game.name}
          className="game-icon"
          onError={(e) => {
            e.currentTarget.src = ImageFallbackThumbnail;
          }}
        />
      </div>
    </div>
  );
}

function MetaBar({
  game,
  onShare,
  showShare,
}: {
  game: Game;
  onShare: () => void;
  showShare: boolean;
}) {
  const primaryCategory = game.categories?.[0];
  const extraCount = (game.categories?.length ?? 1) - 1;

  return (
    <div className="GameMeta">
      <span className="MetaItem">
        <PlayIcon width={12} height={12} fill="#bfbfbf" />
        {formatPlayCount(game.playCount || 0)}
      </span>
      <span className="MetaDot">•</span>
      <span className="MetaItem">
        {primaryCategory?.icon && (
          <Image
            src={primaryCategory.icon}
            className="GenreIcon"
            width={16}
            height={16}
          />
        )}
        {capitalizeFirstLetter(primaryCategory?.name || "Arcade")}
        {extraCount > 0 && ` +${extraCount}`}
      </span>
      {showShare && (
        <>
          <span className="MetaDot">•</span>
          <button className="ShareButton" aria-label="Share" onClick={onShare}>
            <UploadIcon width={12} height={12} />
          </button>
        </>
      )}
    </div>
  );
}

function ModeSelector({
  selected,
  onChange,
}: {
  selected: GameMode;
  onChange: (mode: GameMode) => void;
}) {
  const modes = [
    { mode: GameMode.SOLO, label: "Solo", src: FallbackAvatar },
    { mode: GameMode.PVP, label: "1v1", src: OneVsOneImage },
  ];

  return (
    <div className="mode-selectors" role="group" aria-label="Game mode">
      {modes.map(({ mode, label, src }) => (
        <button
          key={mode}
          type="button"
          className={`mode-card ${selected === mode ? "selected" : ""}`}
          onClick={() => onChange(mode)}
        >
          <div className="selected-check">
            <CheckmarkCircleIcon width={20} height={20} fill="#A667F5" />
          </div>
          <Image
            src={src}
            alt={label}
            className="mode-icon"
            width={64}
            height={64}
          />
          <span className="mode-label">{label}</span>
        </button>
      ))}
    </div>
  );
}

function PlayButton({
  selectedMode,
  isPending,
  onPlay,
}: {
  selectedMode: GameMode;
  isPending: boolean;
  onPlay: () => void;
}) {
  return (
    <div className="action-container">
      <PrimaryActionButton
        onClick={onPlay}
        buttonClassName="play-btn w-100"
        leftImage={
          isPending ? (
            <CustomSpinner size="sm" />
          ) : (
            <PlayIcon fill="#fff" width={24} height={24} />
          )
        }
        text={
          isPending
            ? ""
            : `Play ${selectedMode === GameMode.PVP ? "1v1" : "Solo"}`
        }
        disabled={isPending}
      />
    </div>
  );
}

export default function GameDetailsDrawer() {
  const [drawerSlug, setDrawerSlug] = useAtom(gameDetailsDrawerSlugAtom);
  const { isLoggedIn } = useAuth();
  const { gateAction } = useSubscription();
  const closeDrawer = useCallback(
    () => setDrawerSlug(undefined),
    [setDrawerSlug]
  );

  const {
    data: game,
    isLoading,
    isError,
    refetch,
  } = useGameDetailsBySlugQuery({
    slug: drawerSlug ?? "",
    enabled: !!drawerSlug,
  });

  const [selectedMode, setSelectedMode] = useState<GameMode>(GameMode.PVP);
  const { handleShare } = useShareGameLinkAction({ gameId: game?.id });
  const { handlePlay: startGame, isPending } = useGamePlay(
    game,
    selectedMode,
    closeDrawer
  );

  const isFreeGame = game?.gameType === GameType.FREE;

  const handlePlay = useCallback(() => {
    if (isFreeGame && selectedMode === GameMode.SOLO) {
      startGame();
    } else {
      gateAction(
        () => {
          startGame();
        },
        { onBeforePaywall: closeDrawer }
      );
    }
  }, [isFreeGame, selectedMode, gateAction, closeDrawer, startGame]);

  if (!game) return null;

  return (
    <BottomDrawer
      isOpen={!!drawerSlug}
      onClose={closeDrawer}
      containerClass="GameDetailsDrawerContainer p-0"
      bodyClass="p-0"
      showCloseBtn
      closeBtnStyles={{
        zIndex: 10,
      }}
    >
      {isLoading && (
        <div className="GameDetailsDrawer GameDetailsDrawer--loading">
          <CustomSpinner size="sm" />
        </div>
      )}
      {isError && (
        <div className="GameDetailsDrawer GameDetailsDrawer--error">
          <p>Failed to load game details.</p>
          <PrimaryActionButton
            onClick={() => refetch()}
            text="Retry"
            buttonClassName="retry-btn"
          />
        </div>
      )}
      {game && (
        <div className="GameDetailsDrawer">
          <MediaHeader game={game} />
          <div className="drawer-content">
            <h2 className="game-title">{game.name}</h2>
            <MetaBar game={game} onShare={handleShare} showShare={isLoggedIn} />
            <ModeSelector selected={selectedMode} onChange={setSelectedMode} />
            <PlayButton
              selectedMode={selectedMode}
              isPending={isPending}
              onPlay={handlePlay}
            />
          </div>
        </div>
      )}
    </BottomDrawer>
  );
}
